import { useState } from "react";
import Reveal from "../Reveal";

const tabs = [
  { key: "billing", label: "Billing" },
  { key: "inventory", label: "Inventory" },
  { key: "reports", label: "Reports" },
  { key: "restaurant", label: "Restaurant" },
];

export default function Screenshots() {
  const [active, setActive] = useState("billing");

  return (
    <section data-testid="screenshots-section" className="relative py-24 lg:py-32 bg-navy text-white grain overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <div className="absolute top-1/2 -left-32 h-96 w-96 rounded-full bg-blue-700/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 h-96 w-96 rounded-full bg-blue-800/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Reveal><p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Inside the app</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3">
              A peek at the screens your team will love.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
              Every screen is designed for clarity, speed and zero training time.
            </p>
          </Reveal>
        </div>

        <Reveal delay={180}>
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`screenshot-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  active === t.key
                    ? "bg-blue-800 text-white shadow-lg shadow-blue-800/25"
                    : "bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={220}>
          <div className="mt-12 rounded-3xl border border-white/10 bg-navy/95/60 backdrop-blur-xl p-3 sm:p-4 shadow-2xl">
            <div className="rounded-2xl overflow-hidden bg-navy border border-white/5">
              {active === "billing" && <BillingScreen />}
              {active === "inventory" && <InventoryScreen />}
              {active === "reports" && <ReportsScreen />}
              {active === "restaurant" && <RestaurantScreen />}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BillingScreen() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[420px]">
      <div className="lg:col-span-2 p-6 border-b lg:border-b-0 lg:border-r border-white/5">
        <p className="text-[11px] uppercase tracking-[0.2em] text-blue-700 font-bold">Quick add</p>
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {["Bread", "Milk", "Eggs", "Sugar", "Atta", "Rice", "Oil", "Tea"].map((p, i) => (
            <div key={p} className="rounded-xl bg-white/5 border border-white/10 p-3 hover:bg-blue-800/10 hover:border-blue-800/40 transition cursor-pointer">
              <p className="text-sm font-semibold">{p}</p>
              <p className="text-xs text-slate-400 mt-1">₹{40 + i * 12}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="p-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-blue-700 font-bold">Cart</p>
        <div className="mt-4 space-y-2">
          {[
            { n: "Bread × 2", a: 80 },
            { n: "Milk × 1", a: 62 },
            { n: "Eggs × 12", a: 84 },
          ].map((r) => (
            <div key={r.n} className="flex justify-between text-sm py-2 border-b border-white/5">
              <span>{r.n}</span><span className="font-display font-semibold">₹{r.a}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-xl bg-blue-800/10 border border-blue-800/30 p-4 flex justify-between items-center">
          <span className="text-sm text-slate-300">Total</span>
          <span className="font-display text-2xl font-black text-blue-700">₹226</span>
        </div>
      </div>
    </div>
  );
}

function InventoryScreen() {
  return (
    <div className="p-6 min-h-[420px]">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { l: "Total SKUs", v: "1,284" },
          { l: "Low stock", v: "23" },
          { l: "Out of stock", v: "4" },
          { l: "Stock value", v: "₹8.4L" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl bg-white/5 border border-white/10 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.l}</p>
            <p className="font-display text-xl font-black mt-1">{s.v}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-400 bg-white/5">
          <span className="col-span-5">Item</span><span className="col-span-2">SKU</span>
          <span className="col-span-2">Stock</span><span className="col-span-3">Status</span>
        </div>
        {[
          { n: "Basmati Rice 5kg", s: "GR-001", q: 84, t: "OK" },
          { n: "Sunflower Oil 1L", s: "GR-014", q: 6, t: "Low" },
          { n: "Maggi 70g", s: "GR-082", q: 0, t: "Out" },
          { n: "Lays Classic", s: "SN-023", q: 142, t: "OK" },
        ].map((r) => (
          <div key={r.s} className="grid grid-cols-12 px-4 py-3 text-sm border-t border-white/5">
            <span className="col-span-5 font-medium">{r.n}</span>
            <span className="col-span-2 font-mono text-slate-400">{r.s}</span>
            <span className="col-span-2">{r.q}</span>
            <span className="col-span-3">
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                r.t === "OK" ? "bg-blue-800/15 text-blue-700" :
                r.t === "Low" ? "bg-yellow-500/15 text-yellow-400" :
                "bg-red-500/15 text-red-400"
              }`}>{r.t}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsScreen() {
  return (
    <div className="p-6 min-h-[420px]">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { l: "Revenue", v: "₹4.8L", d: "+12%" },
          { l: "Profit", v: "₹1.2L", d: "+8%" },
          { l: "Expenses", v: "₹38K", d: "+5%" },
          { l: "Avg Bill", v: "₹431", d: "+3%" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl bg-white/5 border border-white/10 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.l}</p>
            <p className="font-display text-2xl font-black mt-1">{s.v}</p>
            <p className="text-xs text-blue-700 font-semibold mt-1">{s.d}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-white/5 border border-white/10 p-5">
        <div className="flex items-end gap-2 h-40">
          {[42, 68, 35, 82, 58, 91, 74, 60, 88, 72, 95, 80].map((h, i) => (
            <div key={i} style={{ height: `${h}%` }} className={`flex-1 rounded-t ${i % 2 === 0 ? "bg-blue-700" : "bg-blue-800/40"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RestaurantScreen() {
  return (
    <div className="p-6 min-h-[420px]">
      <p className="text-[11px] uppercase tracking-[0.2em] text-blue-700 font-bold">Floor view</p>
      <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { n: "T1", s: "Free" }, { n: "T2", s: "Busy" }, { n: "T3", s: "KOT" },
          { n: "T4", s: "Free" }, { n: "T5", s: "Busy" }, { n: "T6", s: "Bill" },
          { n: "T7", s: "Free" }, { n: "T8", s: "Busy" }, { n: "T9", s: "Free" },
          { n: "B1", s: "Bill" }, { n: "B2", s: "Free" }, { n: "B3", s: "KOT" },
        ].map((t) => (
          <div key={t.n} className={`aspect-square rounded-xl grid place-items-center border ${
            t.s === "Free" ? "bg-white/5 border-white/10" :
            t.s === "Busy" ? "bg-blue-800/15 border-blue-800/40" :
            t.s === "KOT" ? "bg-yellow-500/15 border-yellow-500/40" :
            "bg-blue-700/15 border-blue-700/40"
          }`}>
            <div className="text-center">
              <p className="font-display font-bold">{t.n}</p>
              <p className="text-[10px] text-slate-300 mt-1">{t.s}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
